import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Rocket, MessageCircle } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { track } from '@/lib/tracking';
import { whatsappHref, SUPPORT_WHATSAPP } from '@/data/constants';
import { useOverlayOpen } from '@/lib/overlay';
import { usePurchaseBarOpen } from '@/lib/purchaseBar';
import PlansModal from '@/components/PlansModal';

/**
 * ───────────────────────────────────────────────────────────────────────────
 * FLOATING CONVERSION ACTIONS — sticky "Start Now" + WhatsApp.
 * ───────────────────────────────────────────────────────────────────────────
 * Two immediate choices, always reachable while the visitor scrolls:
 *   1. Start Now  -> opens the plan picker (single lead $29 / Basic / Pro /
 *                    Premium) which deep-links into the real checkout flow.
 *   2. WhatsApp   -> opens a prefilled chat with the sales line.
 *
 * Placement rules
 *  - The stack is bottom-RIGHT and anchored with the safe-area inset so it
 *    never sits under an iOS home indicator.
 *  - It lifts above the cookie banner while that banner is on screen, so the
 *    two never overlap (CookieConsent emits `gg:cookie-choice` when dismissed).
 *  - It HIDES completely whenever any modal / drawer / mobile filter sheet is
 *    open (see src/lib/overlay.ts). That registry — not a pile of z-index
 *    numbers — is what guarantees the buttons can never cover a dialog.
 *  - Private app surfaces (dashboard, admin, auth, profile) are excluded: a
 *    signed-in contractor working inside their account does not need a sales
 *    CTA floating over the UI. Every PUBLIC page keeps WhatsApp.
 *
 * Label rules
 *  - The CTA text is CONTEXTUAL, using the four approved variants in each
 *    language: browsing work -> "Get Leads Now", researching a trade/city ->
 *    "View Plans", a contractor landing page -> "Get Started", everywhere else
 *    -> "Start Now". All four come from the cta.* keys, never hardcoded.
 */

/** Route segments that are private app surfaces — no floating sales UI. */
const PRIVATE_SEGMENTS = [
  'dashboard',
  'panel',
  'admin',
  'administrador',
  'login',
  'iniciar-sesion',
  'register',
  'registro',
  'profile',
  'perfil',
  'reset-password',
  'restablecer-contrasena',
];

/** Marketplace / job browsing — the visitor is already looking at leads. */
const LEADS_SEGMENTS = ['jobs', 'trabajos', 'marketplace', 'leads'];

/** Trade + location research pages — show the price ladder. */
const PLANS_SEGMENTS = ['trades', 'oficios', 'california', 'project-directory', 'directorio-de-proyectos'];

/** Contractor landing pages — straight signup intent. */
const CONTRACTOR_SEGMENTS = [
  'general-contractors',
  'contratistas-generales',
  'subcontractors',
  'subcontratistas',
  'memberships',
  'membresias',
  'contractor-directory',
  'directorio-de-contratistas',
];

/**
 * Everything else that still gets the "Start Now" CTA. WhatsApp is not gated
 * by any of these lists — it shows on every public page.
 */
const OTHER_CTA_SEGMENTS = [
  'homeowners',
  'propietarios',
  'how-it-works',
  'como-funciona',
  'resources',
  'recursos',
  'blog',
  'faq',
  'preguntas-frecuentes',
  'help',
  'ayuda',
  'about',
  'acerca-de',
  'contact',
  'contacto',
];

/** Strip the /en or /es prefix and return the remaining segments. */
const segmentsOf = (pathname: string): string[] =>
  pathname.replace(/^\/(en|es)(?=\/|$)/, '').split('/').filter(Boolean);

const FloatingActions: React.FC = () => {
  const { t, lang } = useI18n();
  const location = useLocation();
  const overlayOpen = useOverlayOpen();
  /**
   * A sticky purchase bar (the $29 lead CTA on a project page) owns the bottom
   * of the screen on mobile. When it is up we must NOT float a second orange
   * pill over it: the pill sat exactly on top of the right-hand edge of the pay
   * button. So the sales pill stands down and WhatsApp lifts above the bar.
   */
  const purchaseBar = usePurchaseBarOpen();
  const [plansOpen, setPlansOpen] = useState(false);
  const [cookieBar, setCookieBar] = useState(false);

  // Mirror the cookie banner's visibility so the stack can lift above it.
  useEffect(() => {
    const read = () => {
      try {
        setCookieBar(!localStorage.getItem('bb_cookie_choice'));
      } catch {
        setCookieBar(false);
      }
    };
    read();
    window.addEventListener('gg:cookie-choice', read);
    return () => window.removeEventListener('gg:cookie-choice', read);
  }, []);

  const segs = segmentsOf(location.pathname);
  const first = segs[0] || '';
  const isPrivate = segs.length > 0 && PRIVATE_SEGMENTS.includes(first);
  const isHome = segs.length === 0;
  // The Pricing page already IS the plan list — no floating shortcut needed.
  const isPricing = first === 'pricing' || first === 'precios';

  const inLeads = LEADS_SEGMENTS.includes(first);
  const inPlans = PLANS_SEGMENTS.includes(first);
  const inContractor = CONTRACTOR_SEGMENTS.includes(first);
  const inOther = OTHER_CTA_SEGMENTS.includes(first);

  // The purchase bar IS the CTA while it is up — a second orange pill on top
  // of it is exactly the overlap that hid the pay button on small screens.
  const showStart =
    !isPrivate && !isPricing && !purchaseBar && (isHome || inLeads || inPlans || inContractor || inOther);
  const showWhatsApp = !isPrivate;


  // Contextual label — uses all four approved variants per language.
  const startLabel = inLeads
    ? t('cta.getLeads')
    : inPlans
    ? t('cta.viewPlans')
    : inContractor
    ? t('cta.getStarted')
    : t('cta.startNow');

  // NOTE: when an overlay owns the screen the stack is faded out + made
  // untabbable below rather than unmounted, so the PlansModal it renders can
  // still close itself cleanly.

  if (!showStart && !showWhatsApp) return null;

  const openPlans = () => {
    track('cta_click', { action: 'floating_start', label: startLabel, path: location.pathname });
    setPlansOpen(true);
  };

  const onWhatsApp = () => {
    track('cta_click', { action: 'whatsapp', channel: 'floating', path: location.pathname });
  };

  return (
    <>
      <div
        className={`pointer-events-none fixed right-3 z-[110] flex flex-col items-end gap-2.5 transition-all duration-300 sm:right-5 ${
          purchaseBar
            ? // Lifted clear of the sticky purchase bar (~84px + safe area).
              'bottom-24 sm:bottom-6'
            : cookieBar
            ? 'bottom-32 sm:bottom-28'
            : 'bottom-4 sm:bottom-6'
        } ${overlayOpen ? 'pointer-events-none translate-y-6 opacity-0' : 'opacity-100'}`}
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        aria-hidden={overlayOpen}
      >

        {showStart && (
          <button
            onClick={openPlans}
            tabIndex={overlayOpen ? -1 : 0}
            className="pointer-events-auto flex items-center gap-2 rounded-full bg-orange-500 pl-4 pr-5 py-3.5 text-sm font-extrabold text-white shadow-2xl ring-1 ring-orange-600/20 transition-all duration-200 hover:-translate-y-0.5 hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-300 sm:text-base"
            aria-haspopup="dialog"
          >
            <Rocket className="h-5 w-5 shrink-0" />
            <span>{startLabel}</span>
          </button>
        )}

        {showWhatsApp && (
          <a
            href={whatsappHref(lang)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onWhatsApp}
            tabIndex={overlayOpen ? -1 : 0}
            aria-label={`${t('cta.whatsappAria')} — ${SUPPORT_WHATSAPP.display}`}
            title={`${t('cta.whatsappAria')} — ${SUPPORT_WHATSAPP.display}`}
            className="pointer-events-auto flex items-center gap-2 rounded-full bg-[#25D366] pl-3.5 pr-4 py-3 text-sm font-extrabold text-white shadow-2xl ring-1 ring-emerald-700/20 transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#1eb356] focus:outline-none focus:ring-2 focus:ring-emerald-300"
          >
            <MessageCircle className="h-5 w-5 shrink-0" />
            <span className="hidden sm:inline">{t('cta.whatsapp')}</span>
          </a>
        )}
      </div>

      <PlansModal
        open={plansOpen}
        onClose={() => setPlansOpen(false)}
        source={`floating-cta:${first || 'home'}`}
      />
    </>
  );
};

export default FloatingActions;
